import React, { useEffect } from 'react';
import Dropzone, { IFileWithMeta } from 'react-dropzone-uploader';
import { useDispatch } from 'react-redux';
import * as actions from '../../store/actions';
import ApiEndpoints from '../../store/ApiEndpoints';
import './GraphicsUpload.css';
import DropzoneSubmit from './DropzoneSubmit/DropzoneSubmit';
import DropzonePreview from './DropzonePreview/DropzonePreview';
import { useTypedSelector } from '../../shared/hooks/useTypedSelector';
import { makeStyles } from '@material-ui/core';
import SnackBarType from '../../objects/app/snackbar';
import { useTranslation } from 'react-i18next';

const REMOVED_STATUS = 'removed';
const UPLOAD_DONE_STATUS = 'done';

const useStyles = makeStyles((theme) => ({
    inputContent: {
        textAlign: 'center',
    },
    overlay: {
        width: '100%',
        height: '100%',
        left: '0px',
        top: '0px',
        background: 'white',
        // zIndex: '99',
    },
}));

interface Props {
    onSubmit: () => void;
    customerId: number | null;
    guestId: number | null;
}

const GraphicsUpload: React.FC<Props> = ({ onSubmit, customerId, guestId }) => {
    const MAX_FILE_SIZE = 33554432; // 32MB

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const classes = useStyles();
    const [bulkId, setBulkId] = React.useState<number | null>();
    const [submittedIds, setSubmittedIds] = React.useState<string[]>([]);

    const { success, fileCount } = useTypedSelector(
        (state) => state.graphicUpload.submit
    );

    useEffect(() => {
        // onMount
        setBulkId(Math.floor(Math.random() * 99999999));
    }, []);

    useEffect(() => {
        if (success) {
            onSubmit();
            dispatch(actions.graphicUploadSubmitReset());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success]);

    // specify upload params and url for your files
    const getUploadParams = ({ meta }: any): any => {
        return {
            url: ApiEndpoints.GRAPHIC_UPLOAD(customerId, guestId),
            fields: {
                uploadId: meta.id,
                uploadBulkId: bulkId,
            },
        };
    };

    // called every time a file's `status` changes
    const handleChangeStatus = (
        { meta }: any,
        status: string,
        allFiles: IFileWithMeta[]
    ) => {
        if (status === UPLOAD_DONE_STATUS) {
            const allDone = allFiles.every(
                (file) => file.meta.status === UPLOAD_DONE_STATUS
            );
            if (allDone) {
                dispatch(
                    actions.addSnackbar(
                        t(
                            'All graphics were uploaded, please SUBMIT your graphics to see them in your library.'
                        ),
                        SnackBarType.SUCCESS,
                        false
                    )
                );
            }
            dispatch(actions.graphicUploadSetFileCount(fileCount + 1));
        }
        if (status === REMOVED_STATUS && !submittedIds.includes(meta.id)) {
            dispatch(actions.graphicUploadSetFileCount(fileCount - 1));
            if (bulkId) {
                dispatch(actions.graphicUploadDelete(meta.id, bulkId));
            }
        }
    };

    // receives array of files that are done uploading when submit button is clicked
    const handleSubmit = (files: any[], allFiles: any[]): void => {
        const uploadIds = files.map((f) => f.meta.id);
        setSubmittedIds(uploadIds);
        // onSubmit, allFiles)
        if (bulkId) {
            dispatch(actions.graphicUploadSubmit(uploadIds, bulkId));
        }
    };

    const inputContent = (
        <span className={classes.inputContent} key={1}>
            {t('Drag and drop files, or click to browse.')}
            <br />
            {t('allowed: *.png, *.jpg, *.svg, *.eps')}
        </span>
    );

    return (
        <>
            <Dropzone
                getUploadParams={getUploadParams}
                onChangeStatus={handleChangeStatus}
                inputContent={inputContent}
                submitButtonContent={t('Submit')}
                onSubmit={handleSubmit}
                maxFiles={5}
                SubmitButtonComponent={DropzoneSubmit}
                PreviewComponent={DropzonePreview}
                maxSizeBytes={MAX_FILE_SIZE}
                accept="image/*,application/postscript"
            />
        </>
    );
};

export default GraphicsUpload;
